// Knowledge › Pages (A2 step C1): what the crawl collected for a bot. Ported from chatbot_front hub/sourceReview.js with
// English copy. No imports on purpose: `node --test` loads this file directly (ui/tests/sourceView.test.ts).

/** Below this a page is almost certainly a cookie banner, a redirect stub or an empty template (real pages: 1.9–16 KB). */
export const THIN_BYTES = 500;

export interface SourceRow {
  sourceId?: string;
  url: string;
  version?: number | null;
  bytes?: number | null;
  versions?: number;
  inActiveSnapshot?: boolean;
  origin?: string | null;
  editLocked?: boolean;
  versionAt?: string | null;
}

export function isInline(url: string | null | undefined): boolean {
  return String(url || '').startsWith('inline:');
}

/** Thin pages first — "which pages came out badly?" is what the list is for; never-collected ones above them. */
export function sortForReview<T extends { url: string; bytes?: number | null }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    const ab = a.bytes == null ? -1 : a.bytes;
    const bb = b.bytes == null ? -1 : b.bytes;
    return ab !== bb ? ab - bb : String(a.url).localeCompare(String(b.url));
  });
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return '—';
  return n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`;
}

export function reviewSummary(rows: SourceRow[]): { total: number; thin: number; empty: number; inline: number; inactive: number } {
  let thin = 0;
  let empty = 0;
  let inline = 0;
  let inactive = 0;
  for (const s of rows) {
    if (s.bytes == null) empty += 1;
    else if (s.bytes < THIN_BYTES) thin += 1;
    if (isInline(s.url)) inline += 1;
    if (s.inActiveSnapshot === false) inactive += 1;
  }
  return { total: rows.length, thin, empty, inline, inactive };
}

/**
 * May this edit be saved over the page? The view loads a PREVIEW; saving it would cut the page at the preview length —
 * data loss that looks exactly like a save — so editing must load the full text, and this asks every time.
 */
export function canSaveEdit(loaded: { text: string; truncated?: boolean } | null, draft: string): { ok: boolean; reason: string } {
  if (!loaded) return { ok: false, reason: '' };
  if (loaded.truncated) return { ok: false, reason: 'The full text is not loaded, so it cannot be saved.' };
  if (!draft.trim()) return { ok: false, reason: 'The text cannot be empty — to drop a page, remove it on the next build.' };
  if (draft === loaded.text) return { ok: false, reason: '' };
  return { ok: true, reason: '' };
}

/** Why the crawler leaves this page alone: corrected by a person, or just held. */
export function lockLabel(s: { editLocked?: boolean; origin?: string | null } | null | undefined): string {
  if (!s || !s.editLocked) return '';
  return s.origin === 'manual' ? 'edited by hand' : 'locked';
}

/** The JSON body of a failed hub call (hubService throws `Error(<response text>)`), or null. */
export function hubErrorBody(err: unknown): Record<string, any> | null {
  const msg = err instanceof Error ? err.message : '';
  if (!msg.trimStart().startsWith('{')) return null;
  try {
    const body = JSON.parse(msg);
    return body && typeof body === 'object' ? body : null;
  } catch {
    return null;
  }
}

/** A re-crawl refused because the page was edited by hand: llm-tester's facts (url, version, editedAt), else null. */
export function lockConflict(err: unknown): Record<string, any> | null {
  const b = hubErrorBody(err);
  return b?.code === 'source_edit_locked' ? (b.detail ?? b) : null;
}

const RECHUNK_REFUSALS: Record<string, string> = {
  no_changes:
    'This page’s chunks were already made from the same text and the same prompt — re-chunking would change nothing. Edit the page, re-crawl it, or change the chunking prompt first.',
  bot_not_tagged: 'This bot’s chunks are from before per-page tracking. Run a full re-index once (Snapshots) and per-page re-chunking becomes available.',
  no_active_snapshot: 'This bot has no active snapshot — run a build first.',
};

/** Why a re-chunk was refused, in words to act on (all decided before any LLM call — nothing was spent). */
export function rechunkRefusal(err: unknown): { code: string; text: string } | null {
  const b = hubErrorBody(err);
  const text = b?.code ? RECHUNK_REFUSALS[b.code] : undefined;
  return text ? { code: b!.code, text } : null;
}
